import { FC, useMemo, useState, useCallback } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CustomDropdown from 'components/Dropdown/CustomDropdown';
import AssigneeDropdown from 'components/Dropdown/AssigneeDropdown/dropdown/dropdown';
import DeleteIcon from 'components/Icons/deleteIcon';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AssigneeChipsContainer from 'components/AssigneeChipsContainer';
import { useAssigneesUtils } from 'utils/CustomHooks/useAssigneesUtil';
import { useSelector } from 'react-redux';
import selectors from 'store/selectors';
import {
  AccessItemRow,
  AccessorSelector,
  AccessSelection,
  SelectedAccess,
} from './style';
import {
  TEMPLATE_PERMISSIONS,
  TEMPLATE_ACCESS,
  SUBMISSION_PERMISSIONS,
} from './utils';
import { GroupData } from './types';
import { uniq } from 'lodash';

interface SelectedAccessType {
  people: string[];
  teams: string[];
  locations: string[];
  roles: string[];
}

interface AccessItemProps {
  access: GroupData;
  type: string;
  updateGroupedData: (groupId: string, newGroupData: GroupData) => void;
  removeGroup: (groupId: string) => void;
}

interface TemplatePermissionType {
  value: string;
  label: string;
  description?: string;
}

export const AccessItem: FC<AccessItemProps> = ({
  access,
  updateGroupedData,
  removeGroup,
  type,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { splitAssigneesByIds } = useAssigneesUtils();

  const usersHash = useSelector(selectors.getHashedUserList);
  const teamsHash = useSelector(selectors.getTeamsHash);
  const locationsHash = useSelector(selectors.getLocationsHash);
  const rolesList = useSelector(selectors.getRoles);

  const selectedAccess: SelectedAccessType = useMemo(() => {
    const ids = access.AccessorIds ?? [];
    return splitAssigneesByIds(ids, {
      people: true,
      teams: true,
      locations: true,
      roles: true,
    });
  }, [
    access.AccessorIds,
    splitAssigneesByIds,
    usersHash,
    teamsHash,
    locationsHash,
    rolesList,
  ]);

  // Process selectedAccess to show only 2 items with +n indicator
  const processedShowAccessors = useMemo(() => {
    const allItems = [
      ...(selectedAccess.people ?? []).map((id) => ({ id, type: 'people' })),
      ...(selectedAccess.teams ?? []).map((id) => ({ id, type: 'teams' })),
      ...(selectedAccess.locations ?? []).map((id) => ({
        id,
        type: 'locations',
      })),
      ...(selectedAccess.roles ?? []).map((id) => ({ id, type: 'roles' })),
    ];

    const totalCount = allItems?.length ?? 0;

    if (totalCount <= 1) {
      // Show all items if 2 or fewer
      return {
        people: selectedAccess.people,
        teams: selectedAccess.teams,
        locations: selectedAccess.locations,
        roles: selectedAccess.roles,
        totalCount,
      };
    }

    // Show only first 2 items and calculate remaining count
    const itemsToShow = allItems?.slice(0, 5) ?? [];

    // Reconstruct the object with only the items to show
    const result = {
      people: [] as string[],
      teams: [] as string[],
      locations: [] as string[],
      roles: [] as string[],
      totalCount,
    };

    itemsToShow?.forEach((item) => {
      (
        result[
          item?.type as keyof Omit<typeof result, 'totalCount'>
        ] as string[]
      ).push(item?.id);
    });

    return result;
  }, [selectedAccess]);

  const totalSelected = useMemo(
    () =>
      ['people', 'teams', 'locations', 'roles'].reduce(
        (sum, key) => sum + ((selectedAccess as any)?.[key]?.length || 0),
        0,
      ),
    [selectedAccess],
  );

  const selectedPermission = useMemo<TemplatePermissionType | undefined>(() => {
    const roleValue =
      type === TEMPLATE_ACCESS ? access.role : access.permission;

    const permissions =
      type === TEMPLATE_ACCESS ? TEMPLATE_PERMISSIONS : SUBMISSION_PERMISSIONS;

    return permissions.find(
      (p: TemplatePermissionType) => p.value === roleValue,
    );
  }, [access, type]);

  const handleDropDownOpen = (event: React.MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setAnchorEl(anchorEl ? null : event?.currentTarget);
  };

  const closeDropdown = useCallback(() => setAnchorEl(null), []);

  const handlePermissionSelect = useCallback(
    (item: TemplatePermissionType) => {
      closeDropdown();
      updateGroupedData(access?.groupId, {
        ...access,
        ...(type === TEMPLATE_ACCESS
          ? { role: item?.value }
          : { permission: item?.value }),
      });
    },
    [closeDropdown, access, updateGroupedData],
  );

  const handleAssigneeChange = useCallback(
    (data: {
      all?: boolean;
      selectedList?: {
        people?: string[];
        teams?: string[];
        roles?: string[];
        locations?: string[];
      };
    }) => {
      if (data?.all) {
        updateGroupedData(access?.groupId, {
          ...access,
          AccessorIds: null,
          isEveryone: true,
        });
        return;
      }

      const {
        people = [],
        teams = [],
        roles = [],
        locations = [],
      } = data?.selectedList || {};

      const accessorIds = [
        ...(people ?? []),
        ...(teams ?? []),
        ...(roles ?? []),
        ...(locations ?? []),
      ];

      updateGroupedData(access?.groupId, {
        ...access,
        AccessorIds: uniq(accessorIds),
        isEveryone: false,
      });
    },
    [access, updateGroupedData],
  );

  const handleDelete = useCallback(() => {
    removeGroup(access?.groupId);
  }, [access, removeGroup]);

  const renderPermissionItem = useCallback(
    (item: TemplatePermissionType): JSX.Element => (
      <Box
        key={item?.value}
        className="permissionItem"
        onClick={() => handlePermissionSelect(item)}
        sx={{
          backgroundColor:
            selectedPermission?.value === item?.value
              ? 'rgba(235, 234, 255, 1)'
              : 'white',
        }}
      >
        <Typography
          className="title"
          style={{
            color:
              selectedPermission?.value === item?.value
                ? 'rgba(78, 72, 250, 1)'
                : 'rgba(0, 0, 0, 1)',
          }}
        >
          {item?.label}
        </Typography>
        {item?.description && (
          <Typography className="description">{item?.description}</Typography>
        )}
      </Box>
    ),
    [handlePermissionSelect, selectedPermission],
  );

  return (
    <AccessItemRow direction="row" alignItems="center">
      <Box className="cell assigneesSelection" sx={{ flex: '64%' }}>
        <AssigneeDropdown
          handleChangeCallback={handleAssigneeChange}
          buttonRendererProps={{ style: { width: '100%', height: '100%' } }}
          rootProps={{
            style: {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            },
          }}
          buttonRenderer={
            <AccessorSelector
              direction="row"
              alignItems="center"
              width="100%"
              height="100%"
              position="relative"
            >
              {totalSelected === 0 && !access?.isEveryone ? (
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  width="100%"
                  height="100%"
                >
                  <Typography className="placeholder">
                    Select users, teams, locations or roles
                  </Typography>
                </Stack>
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  width="330px"
                  height="100%"
                  paddingRight="0px"
                  sx={{
                    '& .outer': {
                      overflow: 'hidden',
                    },
                  }}
                >
                  <AssigneeChipsContainer
                    chipsData={processedShowAccessors}
                    boxProps={{ sx: { border: 'unset', p: 0 } }}
                    isDropdownIcon={false}
                    chipSx={{
                      border: '1px solid gray',
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      padding: '8px 7px',
                      height: '27px',
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: '16px',
                      color: 'black',
                    }}
                    allText="All Users, Teams, Locations & Roles"
                    allSelected={access?.isEveryone}
                  />
                  {processedShowAccessors?.totalCount > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: '50px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          lineHeight: '16px',
                          color: 'rgba(97, 97, 97, 1)',
                          fontWeight: 500,
                        }}
                      >
                        ({processedShowAccessors?.totalCount})
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
              <KeyboardArrowDownIcon className="dropdownIcon" />
            </AccessorSelector>
          }
          showTabs
          listProps={{
            all: {
              allText: 'All Users, Teams, Locations & Roles',
              showInlineSelection: true,
              isSearchable: true,
              placeholder: 'Type to Search',
              isMulti: true,
              hideActions: true,
              partition: true,
              showSelectedOnly: true,
              selected: [
                ...(selectedAccess?.people ?? []),
                ...(selectedAccess?.teams ?? []),
                ...(selectedAccess?.locations ?? []),
                ...(selectedAccess?.roles ?? []),
              ],
            },
            people: {
              allText: 'All Users, Teams, Locations & Roles',
              showInlineSelection: false,
              isSearchable: true,
              isMulti: true,
              hideActions: false,
              partition: true,
              selected: selectedAccess?.people ?? [],
            },
            teams: {
              allText: 'All Users, Teams, Locations & Roles',
              showInlineSelection: false,
              isSearchable: true,
              isMulti: true,
              hideActions: false,
              selected: selectedAccess?.teams ?? [],
            },
            locations: {
              allText: 'All Users, Teams, Locations & Roles',
              showInlineSelection: false,
              isSearchable: true,
              isMulti: true,
              hideActions: false,
              selected: selectedAccess?.locations ?? [],
            },
            roles: {
              allText: 'All Users, Teams, Locations & Roles',
              showInlineSelection: false,
              isSearchable: true,
              isMulti: true,
              hideActions: false,
              selected: selectedAccess?.roles ?? [],
            },
          }}
          displayOptions={{
            all: true,
            people: true,
            teams: true,
            locations: true,
            roles: true,
          }}
          popperProps={{
            disablePortal: false,
          }}
          allSelected={access?.isEveryone}
        />
      </Box>

      <Box className="cell" sx={{ flex: '40%' }}>
        <CustomDropdown
          onClickawayCallback={() => setAnchorEl(null)}
          buttonRendererProps={{
            style: {
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            },
          }}
          buttonRenderer={
            <SelectedAccess onClick={handleDropDownOpen}>
              <Stack
                direction="row"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <Typography className="selectedLabel">
                  {selectedPermission?.label ??
                    (type === TEMPLATE_ACCESS
                      ? 'Can only submit'
                      : 'Can view my own submissions')}
                </Typography>
                <KeyboardArrowDownIcon className="dropdownIcon" />
              </Stack>
            </SelectedAccess>
          }
          popperProps={{
            open: Boolean(anchorEl),
            anchorEl,
            placement: 'bottom-start',
            sx: { width: 280, borderRadius: 2, boxShadow: 3 },
            content: (
              <AccessSelection>
                {type === TEMPLATE_ACCESS
                  ? TEMPLATE_PERMISSIONS?.map(renderPermissionItem)
                  : SUBMISSION_PERMISSIONS?.map(renderPermissionItem)}
              </AccessSelection>
            ),
          }}
        />
      </Box>

      <Box className="cell">
        <DeleteIcon
          className="deleteIcon"
          fontSize="small"
          color="inherit"
          onClick={handleDelete}
        />
      </Box>
    </AccessItemRow>
  );
};

export default AccessItem;
